// max-stake.ts — the largest stake the RGS admits for a Dice selection (SC-64981, criterion 6).
//
// THE RULE (rgs-core-runtime #451, max_win_admission.go): the house maximum win is a rule of ADMISSION, never a cut of the
// payout. A stake whose worst-case payout the round's ceiling cannot cover is refused before any debit (MAX_WIN_EXCEEDED).
// The ceiling C, in whole credits, is the engine's backstop (MAX_WIN_CREDITS_V2.dice), lowered to the operator's max win
// where the accepted terms enforce one: floor(maxWinMinor / denomination). The runtime states an enforced ceiling of a
// game that bounds its own payout (Dice) as maxWinSemantics "refuse" (accepted_terms.go maxWinSemanticsOf), of any other
// as "clamp"; an observed or absent one as "none". Either enforcing word lowers C. The worst case of a
// Dice bet is a winning roll of its selection: on a ticket (the v2 math, settled stochastically) the runtime bounds it by
// the CEILING of the exact entitlement (credits.CeilPPM), so
//
//   s_max = the largest b with ceil(b × multiplierPPM / 10^6) ≤ C   ⇔   b ≤ floor(C × 10^6 / multiplierPPM)   (credits)
//
// in minor units b × denomination. The multiplier is the pinned contract's for the selection (diceResolve), never a
// formula of this file; it depends on the winning count alone.
//
// WHERE C COMES FROM: the accepted terms of the pf ticket the roll will be made on — the terms that roll is played under.
// The board (rule) reads the ticket held for the next roll; between a spent ticket and its successor ("unavailable") it keeps
// the last terms seen. Where the roll is sent (forBet) the page asks the SDK which ticket the bet will use (SC-67503,
// sdk.pf.ensure(), SDK 0.36.0): its background work done — a refused bet's ticket retired, what an earlier send left
// unresolved closed — and a usable ticket held (none yet, or an EXPIRED one, asked for again). The stake is checked against
// THAT ticket's terms, every roll, autobet included. The cases the page could not see before — an expired ticket replaced
// inside the bet, a ticket being retired after a refusal, no ticket in time — are the SDK's to settle there. When no usable
// ticket could be held ("unavailable": asking failed, or was rate limited), or the SDK does not answer within
// PREPARE_DEADLINE_MS (the round's own deadline starts only once the roll is sent), the roll is NOT sent: its terms are
// unconfirmed (Codex review: sent, it would ask for a ticket again and go out on terms nobody checked). A session with no
// ticket (legacy rounds, the demo) states no ceiling to the page: the game keeps its own.
//
// What the page still cannot see: a ticket that expires, or whose retirement completes, between ensure()'s answer and the
// bet's own preparation inside sdk.play() — the bet then goes out on its successor. Closing that needs the SDK to check the
// ticket it finally selects (a veto before it sends); until then the RGS's refusal (MAX_WIN_EXCEEDED, no money moved) is the
// bound there. The same window holds for the ticket's math (SC-68793): a known ticket replaced there by one of a math this
// build does not know goes out, and the round's check then says it cannot verify it, never Verified.
//
// SC-68793: the same reading of the ticket decides whether a roll may be sent at all: a ticket naming a math this build does
// not know ("math_unknown") is a round the player could never verify. One read, one deadline.

import { MAX_WIN_CREDITS_V2 } from "@blocklabsdev/rgs-contracts";
import { heldTicketMathUnknown } from "./pf/tickets";
import type { PfTicketPayload } from "@blocklabsdev/rgs-game-manager-frontend-api";
import { resolve } from "./contract";

/** The ceiling fields of the accepted terms a ticket holds (the SDK's AcceptedTerms, as this module reads them). */
export interface HeldCeiling {
  readonly maxWinMinor?: string | number;
  readonly maxWinSemantics?: string;
  readonly denominationMinor?: string | number;
}

const PPM = 1_000_000n;
const ENGINE_CAP: bigint = MAX_WIN_CREDITS_V2.dice!;
const RESOLUTION = 10_000;

const whole = (v: string | number | undefined, what: string): bigint => {
  const s = typeof v === "number" ? String(v) : v;
  if (s === undefined || !/^\d{1,30}$/.test(s)) throw new RangeError(`max stake: ${what} is ${String(v)}`);
  return BigInt(s);
};

/** The largest stake, in minor units, a selection winning on `winCount` of the 10,000 rolls admits under `terms`. */
export function selectionMaxStakeMinor(winCount: number, terms: HeldCeiling | undefined): number {
  if (!Number.isInteger(winCount) || winCount < 1 || winCount >= RESOLUTION) throw new RangeError(`max stake: a win count of ${winCount}`);
  const denomination = terms?.denominationMinor === undefined ? 1n : whole(terms.denominationMinor, "the denomination");
  if (denomination === 0n) throw new RangeError("max stake: the denomination is 0");
  let ceiling = ENGINE_CAP;
  if (terms?.maxWinSemantics === "refuse" || terms?.maxWinSemantics === "clamp") {
    const operator = whole(terms.maxWinMinor, "the max win") / denomination;
    if (operator < ceiling) ceiling = operator;
  }
  // the contract's multiplier for the selection: an "under" target wins on exactly `winCount` rolls
  const ppm = BigInt(resolve(0, 1n, { mode: "under", target: winCount }).multiplierPpm);
  const credits = (ceiling * PPM) / ppm;
  const minor = credits * denomination;
  return minor > BigInt(Number.MAX_SAFE_INTEGER) ? Number.MAX_SAFE_INTEGER : Number(minor);
}

export interface MaxStake {
  /** The vendor's rule: fn(winCount) -> minor units, or null when no ceiling is known (then the game keeps its own). */
  rule(winCount: number): number | null;
  /** Where the roll is sent: the ceiling of the ticket the SDK will make the bet on (`ensure`, sdk.pf.ensure()). null: no
   *  ceiling (legacy rounds, the demo). "unconfirmed": no usable ticket could be held, or the SDK did not answer within
   *  PREPARE_DEADLINE_MS — the roll is not sent. */
  forBet(winCount: number): Promise<number | null | "unconfirmed" | "math_unknown">;
  /** The RGS's own answer for a selection (SC-64981: a MAX_WIN_EXCEEDED refusal states the largest stake that fits it, SDK
   *  0.31+ maxStakeMinor): held — below the terms' own figure — for as long as the same terms are; a new ticket's terms drop
   *  it. The game's ceiling (maxStakeCents) reads it at once, so the board clamps the stake to it. */
  stated(winCount: number, maxStakeMinor: number): void;
}

/** How long a roll waits for the SDK to say which ticket the bet will use before it is refused unsent (the round's own
 *  deadline, ROUND_DEADLINE_MS, starts once it is sent). The SDK may first close what earlier sends left (each call bounded
 *  by its own timeout), so this is generous; a refused roll moved no money. */
export const PREPARE_DEADLINE_MS = 10_000;

/** `p`, or undefined once `ms` have passed. */
async function within<T>(p: Promise<T>, ms: number): Promise<T | undefined> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([p, new Promise<undefined>((resolve) => (timer = setTimeout(() => resolve(undefined), ms)))]);
  } finally {
    clearTimeout(timer);
  }
}

/** The max stake from the tickets: `state` is the SDK's current ticket state, `onChange` calls back on every change,
 *  `ensure` answers the ticket the next bet will be made on (sdk.pf.ensure()). */
export function makeMaxStake(
  state: () => PfTicketPayload | null,
  onChange: (listener: () => void) => void,
  ensure: () => Promise<PfTicketPayload>,
  prepareMs = PREPARE_DEADLINE_MS,
  /** SC-68793: the math identities this build knows (the pinned one, its listed predecessors). Absent: not checked. */
  knownMath?: readonly string[],
): MaxStake {
  let lastTerms: HeldCeiling | undefined | null = null; // null: no ticket seen yet
  const termsOf = (s: PfTicketPayload | null): HeldCeiling | undefined | null => {
    if (s?.mode === "tickets") lastTerms = s.ticket.acceptedTerms;
    else if (s === null || s.mode === "legacy") lastTerms = null; // not on tickets: no ceiling the page knows
    return lastTerms; // "unavailable": the last terms seen (between a spent ticket and its successor)
  };
  const terms = (): HeldCeiling | undefined | null => termsOf(state());
  terms(); // the state the tracker holds now: a ticket already held is the "last terms seen" from the start
  onChange(() => void terms());
  const valid = (winCount: number): boolean => Number.isInteger(winCount) && winCount >= 1 && winCount < RESOLUTION;
  // The RGS's answers, one per selection (winning count), for the terms they were given under (Codex: one slot let a second
  // selection's answer erase the first's).
  // The answers hold for the TERMS they were given under — compared by value: the SDK may hand the same terms in another
  // object (the ticket state the board reads, the ticket sdk.pf.ensure() answers), and that is not new terms.
  const keyOf = (t: HeldCeiling | undefined | null): string =>
    t === null ? "no-ticket" : t === undefined ? "no-terms" : `${t.maxWinMinor ?? ""}|${t.maxWinSemantics ?? ""}|${t.denominationMinor ?? ""}`;
  let said: { key: string; readonly bySelection: Map<number, number> } = { key: keyOf(null), bySelection: new Map() };
  const saidFor = (t: HeldCeiling | undefined | null): Map<number, number> => {
    if (said.key !== keyOf(t)) said = { key: keyOf(t), bySelection: new Map() }; // they answered for other terms
    return said.bySelection;
  };
  const ofTerms = (winCount: number, t: HeldCeiling | undefined | null): number | null => {
    const fromTerms = t === null || !valid(winCount) ? null : selectionMaxStakeMinor(winCount, t);
    const fromRgs = saidFor(t).get(winCount) ?? null;
    return fromRgs === null ? fromTerms : fromTerms === null ? fromRgs : Math.min(fromTerms, fromRgs);
  };
  const of = (winCount: number): number | null => ofTerms(winCount, terms());
  return {
    rule: of,
    stated: (winCount, maxStakeMinor) => {
      if (!valid(winCount) || !Number.isSafeInteger(maxStakeMinor) || maxStakeMinor < 1) return;
      const answers = saidFor(terms());
      answers.set(winCount, Math.min(maxStakeMinor, answers.get(winCount) ?? Infinity));
    },
    forBet: async (winCount) => {
      const next = await within(ensure(), prepareMs);
      if (next === undefined || next.mode === "unavailable") return "unconfirmed"; // no terms anyone checked: not sent
      if (knownMath !== undefined && heldTicketMathUnknown(next, knownMath)) return "math_unknown"; // SC-68793: not sent
      return ofTerms(winCount, termsOf(next));
    },
  };
}
